import {
  EXTRACT_FIELD_TYPE,
  EXTRACT_FIELD_TYPE_OPTIONS,
  EXTRACT_FORM_NAME_SUFFIX,
  INTENT_CONDITION_OPERATOR,
  INTENT_CONDITION_OPERATOR_LABELS,
  INTENT_JUDGE_TYPE,
  INTENT_SYSTEM_FIELDS,
  INTENT_TAG_MODE,
  RATING_CONDITION_OPERATOR,
} from '../constants/intentTag'

export function generateExtractFieldId() {
  return `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/** 信息采集名称：{话术名称}-采集表单 */
export function buildExtractFormName(sceneName) {
  const name = sceneName != null ? String(sceneName).trim() : ''
  if (!name) return ''
  return `${name}-${EXTRACT_FORM_NAME_SUFFIX}`
}

/** 展示/保存用：优先用户自定义名称，否则按话术名称生成默认名 */
export function resolveExtractFormName(intentTag, sceneName) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : {}
  const custom =
    src.extractFormName != null
      ? String(src.extractFormName).trim()
      : src.extract_form_name != null
        ? String(src.extract_form_name).trim()
        : ''
  if (custom) return custom
  return buildExtractFormName(sceneName)
}

export function generateIntentTagId() {
  return `ni_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export function getSystemFieldById(fieldId) {
  return INTENT_SYSTEM_FIELDS.find((item) => item.fieldId === fieldId) || null
}

export function getSystemFieldValuePlaceholder(field) {
  if (!field) return '输入值'
  if (field.valuePlaceholder) return field.valuePlaceholder
  if (field.type === EXTRACT_FIELD_TYPE.NUMBER) {
    return field.unit ? `输入${field.unit}数` : '输入数值'
  }
  return '输入值'
}

export function getIntentOperatorLabel(operator) {
  return INTENT_CONDITION_OPERATOR_LABELS[operator] || operator || '—'
}

export function defaultOpForSystemFieldId(fieldId) {
  const field = getSystemFieldById(fieldId)
  if (field && field.type === EXTRACT_FIELD_TYPE.NUMBER) {
    return INTENT_CONDITION_OPERATOR.GTE
  }
  return INTENT_CONDITION_OPERATOR.EQ
}

export function getOpsForSystemField(field) {
  if (!field) return [INTENT_CONDITION_OPERATOR.EQ, INTENT_CONDITION_OPERATOR.NEQ]
  if (field.type === EXTRACT_FIELD_TYPE.ENUM) {
    return [INTENT_CONDITION_OPERATOR.EQ]
  }
  if (field.type === EXTRACT_FIELD_TYPE.NUMBER) {
    return [
      INTENT_CONDITION_OPERATOR.GT,
      INTENT_CONDITION_OPERATOR.LT,
      INTENT_CONDITION_OPERATOR.EQ,
      INTENT_CONDITION_OPERATOR.GTE,
      INTENT_CONDITION_OPERATOR.LTE,
    ]
  }
  return [INTENT_CONDITION_OPERATOR.EQ, INTENT_CONDITION_OPERATOR.NEQ]
}

export function getOpsForCollectField(field) {
  if (!field) return [INTENT_CONDITION_OPERATOR.EQ, INTENT_CONDITION_OPERATOR.NEQ]
  if (field.type === EXTRACT_FIELD_TYPE.ENUM) {
    return [INTENT_CONDITION_OPERATOR.EQ, INTENT_CONDITION_OPERATOR.NEQ]
  }
  if (field.type === EXTRACT_FIELD_TYPE.NUMBER) {
    return [
      INTENT_CONDITION_OPERATOR.EQ,
      INTENT_CONDITION_OPERATOR.NEQ,
      INTENT_CONDITION_OPERATOR.GT,
      INTENT_CONDITION_OPERATOR.LT,
      INTENT_CONDITION_OPERATOR.GTE,
      INTENT_CONDITION_OPERATOR.LTE,
      INTENT_CONDITION_OPERATOR.FILLED,
      INTENT_CONDITION_OPERATOR.EMPTY,
    ]
  }
  return [
    INTENT_CONDITION_OPERATOR.EQ,
    INTENT_CONDITION_OPERATOR.NEQ,
    INTENT_CONDITION_OPERATOR.CONTAINS,
    INTENT_CONDITION_OPERATOR.FILLED,
    INTENT_CONDITION_OPERATOR.EMPTY,
  ]
}

export function isSystemFieldId(fieldId) {
  return INTENT_SYSTEM_FIELDS.some((item) => item.fieldId === fieldId)
}

/** 从信息采集中移除系统指标（系统指标只出现在意向/评级规则里） */
export function ensureForcedSystemExtractFields(intentTag) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : null
  if (!src) return createDefaultIntentTagState()
  if (!Array.isArray(src.extractFields)) {
    src.extractFields = []
  }
  src.extractFields = src.extractFields.filter((item) => !item || !isSystemFieldId(item.fieldId))
  return src
}

export function findRatingRuleField(fieldId, extractFields) {
  const systemField = getSystemFieldById(fieldId)
  if (systemField) {
    return { ...systemField, source: 'system' }
  }
  const list = Array.isArray(extractFields) ? extractFields : []
  const collectField = list.find(
    (item) => item && String(item.fieldId || '') === String(fieldId || '') && !isSystemFieldId(item.fieldId),
  )
  if (collectField) {
    return { ...collectField, source: 'collect' }
  }
  return null
}

export function getOpsForRatingRuleField(fieldId, extractFields) {
  const field = findRatingRuleField(fieldId, extractFields)
  if (!field) return [INTENT_CONDITION_OPERATOR.EQ]
  if (field.source === 'system') return getOpsForSystemField(field)
  return getOpsForCollectField(field)
}

export function defaultOpForRatingRuleFieldId(fieldId, extractFields) {
  const field = findRatingRuleField(fieldId, extractFields)
  if (field && field.source === 'collect' && field.type === EXTRACT_FIELD_TYPE.TEXT) {
    return INTENT_CONDITION_OPERATOR.FILLED
  }
  return INTENT_CONDITION_OPERATOR.EQ
}

function normalizeRatingConditionOperator(operatorRaw) {
  const raw = operatorRaw != null ? String(operatorRaw) : INTENT_CONDITION_OPERATOR.EQ
  if (raw === RATING_CONDITION_OPERATOR.COLLECTED || raw === 'collected') {
    return INTENT_CONDITION_OPERATOR.FILLED
  }
  const allowed = Object.values(INTENT_CONDITION_OPERATOR)
  return allowed.includes(raw) ? raw : INTENT_CONDITION_OPERATOR.EQ
}

export function normalizeRatingConditionForField(condition, extractFields) {
  const next = {
    fieldId: condition && condition.fieldId != null ? String(condition.fieldId) : '',
    operator:
      condition && condition.operator != null
        ? normalizeRatingConditionOperator(condition.operator)
        : INTENT_CONDITION_OPERATOR.EQ,
    value: condition && condition.value != null ? String(condition.value) : '',
  }
  const field = findRatingRuleField(next.fieldId, extractFields)
  const ops = getOpsForRatingRuleField(next.fieldId, extractFields)
  if (!ops.includes(next.operator)) {
    next.operator = field ? defaultOpForRatingRuleFieldId(next.fieldId, extractFields) : INTENT_CONDITION_OPERATOR.EQ
  }
  if (
    next.operator === INTENT_CONDITION_OPERATOR.FILLED ||
    next.operator === INTENT_CONDITION_OPERATOR.EMPTY
  ) {
    next.value = ''
  }
  if (field && field.type === EXTRACT_FIELD_TYPE.ENUM) {
    const allowed = Array.isArray(field.allowedValues) ? field.allowedValues : []
    if (allowed.length && allowed.indexOf(next.value) < 0) {
      next.value = ''
    }
  }
  return next
}

function normalizeIntentCondition(raw) {
  if (!raw || typeof raw !== 'object') {
    return { fieldId: '', operator: INTENT_CONDITION_OPERATOR.EQ, value: '' }
  }
  const operatorRaw = raw.operator != null ? String(raw.operator) : INTENT_CONDITION_OPERATOR.EQ
  const allowedOps = Object.values(INTENT_CONDITION_OPERATOR)
  const operator = allowedOps.includes(operatorRaw) ? operatorRaw : INTENT_CONDITION_OPERATOR.EQ
  return {
    fieldId:
      raw.field_id != null
        ? String(raw.field_id)
        : raw.fieldId != null
          ? String(raw.fieldId)
          : '',
    operator,
    value: raw.value != null ? String(raw.value) : '',
  }
}

function normalizeIntentTag(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      id: generateIntentTagId(),
      name: '',
      judge: INTENT_JUDGE_TYPE.PROMPT,
      rule: '',
      conditions: [],
      ranking: null,
    }
  }
  const judgeRaw = raw.judge != null ? String(raw.judge) : raw.judge_type != null ? String(raw.judge_type) : ''
  const hasConditions = Array.isArray(raw.conditions) && raw.conditions.length > 0
  const judge =
    judgeRaw === INTENT_JUDGE_TYPE.RULE || (judgeRaw !== INTENT_JUDGE_TYPE.PROMPT && hasConditions)
      ? INTENT_JUDGE_TYPE.RULE
      : INTENT_JUDGE_TYPE.PROMPT
  const conditionsRaw = Array.isArray(raw.conditions) ? raw.conditions : []
  return {
    id: raw.id != null ? String(raw.id) : generateIntentTagId(),
    name:
      raw.tag_name != null
        ? String(raw.tag_name)
        : raw.name != null
          ? String(raw.name)
          : '',
    judge,
    rule:
      raw.tag_description != null
        ? String(raw.tag_description)
        : raw.rule != null
          ? String(raw.rule)
          : raw.desc != null
            ? String(raw.desc)
            : '',
    conditions: conditionsRaw.map(normalizeIntentCondition),
    ranking: parseRanking(raw.ranking != null ? raw.ranking : raw.rank),
  }
}

export function createDefaultIntentTagState() {
  return {
    mode: INTENT_TAG_MODE.REGULAR,
    rules: [],
    extractFormName: '',
    extractFormId: '',
    extractFields: [],
    ratingRules: [],
  }
}

export function getExtractFieldTypeLabel(type) {
  const hit = EXTRACT_FIELD_TYPE_OPTIONS.find((item) => item.value === type)
  return hit ? hit.label : type
}

/** 列表展示用短标签：枚举 / 文本 / 数值 */
export function getExtractFieldTypeShortLabel(type) {
  if (type === EXTRACT_FIELD_TYPE.ENUM) return '枚举'
  if (type === EXTRACT_FIELD_TYPE.TEXT) return '文本'
  if (type === EXTRACT_FIELD_TYPE.NUMBER) return '数值'
  return getExtractFieldTypeLabel(type)
}

export function getRatingOperatorLabel(operator) {
  return getIntentOperatorLabel(normalizeRatingConditionOperator(operator))
}

function parseCollectForm(raw) {
  if (raw == null || typeof raw !== 'object') return true
  const v = raw.collect_form != null ? raw.collect_form : raw.collectForm
  if (v === false || v === 0 || v === '0' || v === 'no' || v === '否') return false
  return true
}

export function isExtractFieldCollectForm(field) {
  return parseCollectForm(field)
}

function normalizeExtractField(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      fieldId: generateExtractFieldId(),
      name: '',
      type: EXTRACT_FIELD_TYPE.ENUM,
      allowedValues: [],
      extractInstruction: '',
      collectForm: true,
    }
  }
  const fieldId =
    raw.field_id != null
      ? String(raw.field_id)
      : raw.fieldId != null
        ? String(raw.fieldId)
        : generateExtractFieldId()
  const typeRaw = raw.field_type != null ? raw.field_type : raw.type
  const type =
    typeRaw === EXTRACT_FIELD_TYPE.TEXT ||
    typeRaw === EXTRACT_FIELD_TYPE.NUMBER ||
    typeRaw === EXTRACT_FIELD_TYPE.ENUM
      ? typeRaw
      : EXTRACT_FIELD_TYPE.ENUM
  const allowedRaw = Array.isArray(raw.allowed_values)
    ? raw.allowed_values
    : Array.isArray(raw.allowedValues)
      ? raw.allowedValues
      : []
  return {
    fieldId,
    name: raw.field_name != null ? String(raw.field_name) : raw.name != null ? String(raw.name) : '',
    type,
    allowedValues: allowedRaw.map((item) => String(item)).filter(Boolean),
    extractInstruction:
      raw.extract_instruction != null
        ? String(raw.extract_instruction)
        : raw.extractInstruction != null
          ? String(raw.extractInstruction)
          : '',
    collectForm: parseCollectForm(raw),
  }
}

function normalizeRatingCondition(raw) {
  if (!raw || typeof raw !== 'object') {
    return { fieldId: '', operator: INTENT_CONDITION_OPERATOR.EQ, value: '' }
  }
  return {
    fieldId:
      raw.field_id != null
        ? String(raw.field_id)
        : raw.fieldId != null
          ? String(raw.fieldId)
          : '',
    operator: normalizeRatingConditionOperator(raw.operator),
    value: raw.value != null ? String(raw.value) : '',
  }
}

export function parseRanking(raw) {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.floor(n)
}

export function sortItemsByRanking(items) {
  if (!Array.isArray(items)) return items
  items.sort((a, b) => {
    const ra = parseRanking(a && a.ranking)
    const rb = parseRanking(b && b.ranking)
    if (ra == null && rb == null) return 0
    if (ra == null) return 1
    if (rb == null) return -1
    return ra - rb
  })
  return items
}

/** 按当前列表顺序将 ranking 重排为 1..n */
export function resetItemRankings(items) {
  if (!Array.isArray(items)) return items
  items.forEach((item, index) => {
    if (item && typeof item === 'object') {
      item.ranking = index + 1
    }
  })
  return items
}

/** 先按已有 ranking 排序，再重排为 1..n */
export function ensureItemRankings(items) {
  sortItemsByRanking(items)
  return resetItemRankings(items)
}

export const parseRatingRanking = parseRanking
export const sortRatingRulesByRanking = sortItemsByRanking
export const resetRatingRuleRankings = resetItemRankings
export const ensureRatingRuleRankings = ensureItemRankings

function normalizeRatingRule(raw) {
  if (!raw || typeof raw !== 'object') {
    return { name: '', conditions: [], ranking: null }
  }
  const conditionsRaw = Array.isArray(raw.conditions) ? raw.conditions : []
  return {
    name: raw.rule_name != null ? String(raw.rule_name) : raw.name != null ? String(raw.name) : '',
    ranking: parseRatingRanking(raw.ranking != null ? raw.ranking : raw.rank),
    conditions: conditionsRaw.map(normalizeRatingCondition),
  }
}

/** 从 intent_and_tags 读取信息采集（兼容旧版 extract_form_name + extract_fields 扁平结构） */
function parseExtractFormFromIntentTab(src) {
  const raw = src && typeof src === 'object' ? src : {}

  if (raw.extract_form && typeof raw.extract_form === 'object') {
    const form = raw.extract_form
    const fieldsRaw = Array.isArray(form.form_fields)
      ? form.form_fields
      : Array.isArray(form.fields)
        ? form.fields
        : []
    return {
      formName: form.form_name != null ? String(form.form_name).trim() : '',
      formId:
        form.form_id != null
          ? String(form.form_id).trim()
          : form.id != null
            ? String(form.id).trim()
            : '',
      fields: fieldsRaw.map(normalizeExtractField),
    }
  }

  return {
    formName: raw.extract_form_name != null ? String(raw.extract_form_name).trim() : '',
    formId:
      raw.extract_form_id != null
        ? String(raw.extract_form_id).trim()
        : raw.extractFormId != null
          ? String(raw.extractFormId).trim()
          : '',
    fields: Array.isArray(raw.extract_fields) ? raw.extract_fields.map(normalizeExtractField) : [],
  }
}

function buildExtractFormFieldsSnapshot(fields) {
  return (fields || [])
    .filter((field) => field && !isSystemFieldId(field.fieldId))
    .map((field) => ({
    field_id: String(field.fieldId || ''),
    field_name: String(field.name != null ? field.name : '').trim(),
    field_type: field.type || EXTRACT_FIELD_TYPE.ENUM,
    allowed_values:
      field.type === EXTRACT_FIELD_TYPE.ENUM
        ? (field.allowedValues || []).map((item) => String(item).trim()).filter(Boolean)
        : [],
    extract_instruction: String(field.extractInstruction != null ? field.extractInstruction : '').trim(),
    collect_form: isExtractFieldCollectForm(field),
  }))
}

function buildExtractFormSnapshot(intentTag, sceneName, mode) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : createDefaultIntentTagState()
  ensureForcedSystemExtractFields(src)
  const formId = src.extractFormId != null ? String(src.extractFormId).trim() : ''
  const customName = src.extractFormName != null ? String(src.extractFormName).trim() : ''
  const formFields = buildExtractFormFieldsSnapshot(src.extractFields)
  // 切回常规模式时仍保留已配置的采集字段；只有从未配置过才写空表单
  if (mode !== INTENT_TAG_MODE.FORCED && !formId && !customName && !formFields.length) {
    return { form_id: '', form_name: '', form_fields: [] }
  }
  return {
    form_id: formId,
    form_name: resolveExtractFormName(src, sceneName),
    form_fields: formFields,
  }
}

export function applyIntentTagFromSnapshot(intentTab, target) {
  const next = target && typeof target === 'object' ? target : createDefaultIntentTagState()
  const src = intentTab && typeof intentTab === 'object' ? intentTab : {}

  const modeRaw = src.intent_mode != null ? src.intent_mode : src.mode
  next.mode =
    modeRaw === INTENT_TAG_MODE.FORCED ? INTENT_TAG_MODE.FORCED : INTENT_TAG_MODE.REGULAR

  next.rules = Array.isArray(src.intent_tags) ? src.intent_tags.map(normalizeIntentTag) : []
  ensureItemRankings(next.rules)

  const extractForm = parseExtractFormFromIntentTab(src)
  next.extractFormName = extractForm.formName
  next.extractFormId = extractForm.formId
  next.extractFields = extractForm.fields

  next.ratingRules = Array.isArray(src.rating_rules)
    ? src.rating_rules.map(normalizeRatingRule)
    : []
  ensureRatingRuleRankings(next.ratingRules)

  if (next.mode === INTENT_TAG_MODE.FORCED) {
    ensureForcedSystemExtractFields(next)
  }

  return next
}

export function buildIntentTagSnapshot(intentTag, sceneName) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : createDefaultIntentTagState()
  const mode =
    src.mode === INTENT_TAG_MODE.FORCED ? INTENT_TAG_MODE.FORCED : INTENT_TAG_MODE.REGULAR

  return {
    intent_mode: mode,
    extract_form: buildExtractFormSnapshot(src, sceneName, mode),
    intent_tags: (src.rules || []).map((r, index) => {
      const judge =
        r && r.judge === INTENT_JUDGE_TYPE.RULE ? INTENT_JUDGE_TYPE.RULE : INTENT_JUDGE_TYPE.PROMPT
      const base = {
        tag_name: String(r && r.name != null ? r.name : '').trim(),
        judge,
        ranking: parseRanking(r && r.ranking) || index + 1,
      }
      if (judge === INTENT_JUDGE_TYPE.RULE) {
        return {
          ...base,
          tag_description: '',
          conditions: (r.conditions || []).map((cond) => ({
            field_id: String(cond.fieldId || ''),
            operator: String(cond.operator || INTENT_CONDITION_OPERATOR.EQ),
            value: String(cond.value != null ? cond.value : '').trim(),
          })),
        }
      }
      return {
        ...base,
        tag_description: String(r && r.rule != null ? r.rule : '').trim(),
        conditions: [],
      }
    }),
    rating_rules: (src.ratingRules || []).map((rule, index) => ({
      ranking: parseRanking(rule && rule.ranking) || index + 1,
      rule_name: String(rule && rule.name != null ? rule.name : '').trim(),
      conditions: (rule.conditions || []).map((cond) => ({
        field_id: String(cond.fieldId || ''),
        operator: normalizeRatingConditionOperator(cond.operator),
        value:
          cond.operator === INTENT_CONDITION_OPERATOR.FILLED ||
          cond.operator === INTENT_CONDITION_OPERATOR.EMPTY ||
          cond.operator === RATING_CONDITION_OPERATOR.COLLECTED
            ? ''
            : String(cond.value != null ? cond.value : '').trim(),
      })),
    })),
  }
}

export function formatIntentConditionText(condition) {
  const field = getSystemFieldById(condition.fieldId)
  const fieldName = field && field.name ? field.name : '未知指标'
  const operator = condition.operator || INTENT_CONDITION_OPERATOR.EQ
  const opLabel = getIntentOperatorLabel(operator)
  if (operator === INTENT_CONDITION_OPERATOR.FILLED || operator === INTENT_CONDITION_OPERATOR.EMPTY) {
    return `${fieldName} ${opLabel}`
  }
  const value = condition.value != null ? String(condition.value) : ''
  return `${fieldName} ${opLabel}${value ? ` ${value}` : ''}`
}

export function formatIntentTagSummary(item) {
  if (!item || typeof item !== 'object') return '—'
  if (item.judge === INTENT_JUDGE_TYPE.RULE) {
    const conditions = Array.isArray(item.conditions) ? item.conditions : []
    if (!conditions.length) return '（未配置条件）'
    return conditions.map((cond) => formatIntentConditionText(cond)).join(' 且 ')
  }
  const desc = item.rule != null ? String(item.rule).trim() : ''
  return desc || '—'
}

export function formatRatingConditionText(condition, extractFields) {
  const field = findRatingRuleField(condition.fieldId, extractFields)
  const fieldName = field && field.name ? field.name : '未知字段'
  const operator = normalizeRatingConditionOperator(condition.operator)
  const opLabel = getIntentOperatorLabel(operator)
  if (operator === INTENT_CONDITION_OPERATOR.FILLED || operator === INTENT_CONDITION_OPERATOR.EMPTY) {
    return `${fieldName} ${opLabel}`
  }
  const value = condition.value != null ? String(condition.value) : ''
  return `${fieldName} ${opLabel}${value ? ` ${value}` : ''}`
}

export function formatRatingRuleLogic(rule, extractFields) {
  const conditions = rule && Array.isArray(rule.conditions) ? rule.conditions : []
  if (!conditions.length) return '—'
  return conditions.map((cond) => formatRatingConditionText(cond, extractFields)).join(' 且 ')
}

/** 强制收取模式下，信息采集是否有可同步的字段 */
export function hasForcedExtractFormData(intentTag) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : {}
  if (src.mode !== INTENT_TAG_MODE.FORCED) return false
  return Array.isArray(src.extractFields) && src.extractFields.length > 0
}

function mapExtractFieldToCrmFormColumn(field) {
  const normalized = normalizeExtractField(field)
  const title = String(normalized.name || '').trim() || '未命名'
  const dataIndex = String(normalized.fieldId || generateExtractFieldId())
  const tip = String(normalized.extractInstruction || '').trim()
  const collect_form = isExtractFieldCollectForm(normalized)

  if (normalized.type === EXTRACT_FIELD_TYPE.ENUM) {
    const values = (normalized.allowedValues || []).map((item) => String(item).trim()).filter(Boolean)
    const valueEnum = {}
    const options = values.map((val) => {
      valueEnum[val] = val
      return { label: val, value: val }
    })
    return {
      valueType: 'radio',
      title,
      dataIndex,
      tip,
      collect_form,
      valueEnum,
      fieldProps: { options },
    }
  }

  return {
    valueType: 'input',
    title,
    dataIndex,
    tip,
    collect_form,
  }
}

/** 解析 CRM 表单 content（可能是 JSON 字符串或对象） */
export function parseCrmFormContent(raw) {
  if (raw == null || raw === '') return {}
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch (e) {
      return {}
    }
  }
  return {}
}

/**
 * 将信息采集字段映射为 CRM 表单 content（与表单定义页结构一致）。
 * 若传入 existingContent，则保留其中的推送相关字段（pushApi / push_grade）及 config。
 *
 * 注意：无论 collect_form 为 true/false，字段都必须写入 columns；
 * collect_form 仅供 v2 端控制展示/隐藏，不可在此过滤。
 */
export function buildCrmFormContentFromExtractFields(extractFields, existingContent) {
  // 只排除系统字段；不要按 isExtractFieldCollectForm 过滤
  const columns = (Array.isArray(extractFields) ? extractFields : [])
    .filter((field) => field && !isSystemFieldId(field.fieldId))
    .map(mapExtractFieldToCrmFormColumn)
  const prev = parseCrmFormContent(existingContent)
  const next = {
    columns,
    config:
      prev.config && typeof prev.config === 'object' && !Array.isArray(prev.config)
        ? prev.config
        : {},
    pushApi: prev.pushApi != null ? prev.pushApi : '',
  }
  if (prev.push_grade != null && prev.push_grade !== '') {
    next.push_grade = prev.push_grade
  }
  return next
}

/** 从表单详情/列表项中提取推送相关顶层字段，避免 update 时被覆盖清空 */
export function pickCrmFormPushTopFields(formRecord) {
  if (!formRecord || typeof formRecord !== 'object') return {}
  const next = {}
  ;['push_condition', 'is_push_columns', 'auto_push'].forEach((key) => {
    if (formRecord[key] != null) next[key] = formRecord[key]
  })
  return next
}

/**
 * 用已有表单记录合并 update payload，保留推送设置。
 * 话术保存只会同步采集字段 columns，不应清空 CRM 侧已配置的推送地址/规则等。
 */
export function mergeCrmFormUpdatePayload(basePayload, existingForm) {
  if (!basePayload || typeof basePayload !== 'object') return basePayload
  if (!existingForm || typeof existingForm !== 'object') return basePayload
  const builtColumns =
    (basePayload.content && Array.isArray(basePayload.content.columns)
      ? basePayload.content.columns
      : []) || []
  return {
    ...basePayload,
    ...pickCrmFormPushTopFields(existingForm),
    content: {
      ...buildCrmFormContentFromExtractFields([], existingForm.content),
      columns: builtColumns,
    },
  }
}

/** 将 CRM 表单 id 转为 int（后端 CustomFormParam.id 为 int64） */
export function toCrmFormIdNumber(raw) {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.trunc(n)
}

/** 构建 /api/crm/form/add|update 请求体 */
export function buildCrmFormPayload(intentTag, sceneName, existingForm) {
  const src = intentTag && typeof intentTag === 'object' ? intentTag : createDefaultIntentTagState()
  const name = resolveExtractFormName(src, sceneName) || '采集表单'
  const formId = toCrmFormIdNumber(src.extractFormId)
  const content = buildCrmFormContentFromExtractFields(
    src.extractFields,
    existingForm && existingForm.content,
  )
  if (formId != null) {
    return {
      id: formId,
      name,
      content,
      ...pickCrmFormPushTopFields(existingForm),
    }
  }
  return { name, content, is_llm: 1 }
}

/** 从 form/list/page 响应中按名称精确匹配已有表单 */
export function findCrmFormByNameFromListResponse(res, formName) {
  const target = formName != null ? String(formName).trim() : ''
  if (!target || !res || typeof res !== 'object') return null
  const data = res.data
  const list = Array.isArray(data)
    ? data
    : data && Array.isArray(data.data)
      ? data.data
      : data && Array.isArray(data.list)
        ? data.list
        : []
  const hit = list.find((item) => item && String(item.name || '').trim() === target)
  return hit || null
}

/** 从 form/add|update 响应中解析表单 id */
export function parseCrmFormIdFromResponse(res) {
  if (!res || typeof res !== 'object') return ''
  const data = res.data
  if (data == null) return ''
  if (typeof data === 'object') {
    if (data.id != null && String(data.id).trim() !== '') return String(data.id).trim()
    if (data.form_id != null && String(data.form_id).trim() !== '') return String(data.form_id).trim()
  }
  if (typeof data === 'number' || typeof data === 'string') {
    const id = String(data).trim()
    return id && id !== 'undefined' && id !== 'null' ? id : ''
  }
  return ''
}

/** 从 form/scene/list 响应中解析绑定列表 */
export function parseCrmFormSceneListFromResponse(res) {
  if (!res || typeof res !== 'object') return []
  const data = res.data
  if (Array.isArray(data)) return data
  if (data && Array.isArray(data.data)) return data.data
  if (data && Array.isArray(data.list)) return data.list
  return []
}
